import React, { useEffect, useState, useRef } from 'react';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../i18n';

const GoogleReviews = () => {
  const [reviews, setReviews] = useState([]);
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/content/google-reviews`);
        const data = await response.json();
        setReviews(data);
      } catch (error) {
        console.error("Erro ao carregar reviews:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchReviews();
  }, []);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const { scrollLeft, clientWidth } = scrollRef.current;
      // Ajuste de scroll: em mobile move menos, em desktop move mais
      const moveDistance = window.innerWidth < 768 ? clientWidth * 0.9 : clientWidth * 0.4;
      const scrollTo = direction === 'left' ? scrollLeft - moveDistance : scrollLeft + moveDistance;
      scrollRef.current.scrollTo({ left: scrollTo, behavior: 'smooth' });
    }
  };

  if (loading) return <p className="text-center py-10">Carregando...</p>;
  if (reviews.length === 0) return <p className="text-center py-10 text-red-500">Nenhum review encontrado na URL: {process.env.REACT_APP_BACKEND_URL}</p>;;

  return (
    <section className="bg-white py-20 relative px-4 md:px-12 lg:px-20 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        
        {/* Títulos com "Experiências Reais" */}
        <div className="text-center mb-12">
          <h4 className="text-[#BFA05F] uppercase tracking-[0.2em] text-[10px] md:text-xs font-semibold mb-2">
            {language === 'pt' ? 'Experiências Reais' : 'Real Experiences'}
          </h4>
          <h2 className="font-heading text-2xl md:text-4xl font-semibold text-[#1A1815] mb-4">
            {language === 'pt' ? 'O que dizem nossas clientes' : 'What our clients say'}
          </h2>
          <div className="flex justify-center items-center gap-1">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-4 h-4 fill-[#FABB05] text-[#FABB05]" />
            ))}
            <span className="ml-2 text-sm font-medium text-gray-500">5.0 Google Rating</span>
          </div>
        </div>

        {/* Container do Carrossel */}
        <div className="relative px-2 md:px-4">
          
          {/* Seta Esquerda - Sempre Visível */}
          <button 
            onClick={() => scroll('left')}
            className="absolute -left-2 md:-left-6 top-1/2 -translate-y-1/2 z-30 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full shadow-md flex items-center justify-center border border-gray-100 active:scale-95 transition-all"
            aria-label="Previous"
          >
            <ChevronLeft className="w-5 h-5 text-gray-700" />
          </button>

          {/* Seta Direita - Sempre Visível */}
          <button 
            onClick={() => scroll('right')}
            className="absolute -right-2 md:-right-6 top-1/2 -translate-y-1/2 z-30 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full shadow-md flex items-center justify-center border border-gray-100 active:scale-95 transition-all"
            aria-label="Next"
          >
            <ChevronRight className="w-5 h-5 text-gray-700" />
          </button>

          {/* Lista de Cards */}
          <div 
            ref={scrollRef}
            className="flex gap-4 md:gap-6 overflow-x-auto pb-8 snap-x snap-mandatory no-scrollbar touch-pan-x"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            <style>{`.no-scrollbar::-webkit-scrollbar { display: none; }`}</style>
            
            {reviews.map((review, index) => (
              <div 
                key={index}
                className="min-w-[88%] sm:min-w-[45%] lg:min-w-[380px] snap-center bg-[#F8F9FA] p-6 md:p-8 rounded-3xl border border-gray-50 flex flex-col justify-between hover:bg-white hover:shadow-xl hover:border-gray-100 transition-all duration-300"
              >
                <div>
                  <div className="flex justify-between items-start mb-6">
                    <div className="flex items-center gap-4">
                      <img 
                        src={review.profile_photo_url} 
                        alt={review.author_name}
                        className="w-12 h-12 md:w-14 md:h-14 rounded-full object-cover border-2 border-white shadow-sm"
                      />
                      <div>
                        <h5 className="font-bold text-sm md:text-base text-gray-900 leading-tight">{review.author_name}</h5>
                        <p className="text-[10px] text-gray-400 uppercase tracking-widest mt-1">{review.relative_time_description}</p>
                      </div>
                    </div>
                    {/* Google G Icon */}
                    <div className="bg-white p-1.5 rounded-lg shadow-sm">
                      <svg viewBox="0 0 24 24" width="16" height="16">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.66l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                    </div>
                  </div>

                  <div className="flex gap-0.5 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className={`w-4 h-4 ${i < review.rating ? 'fill-[#FABB05] text-[#FABB05]' : 'text-gray-200'}`} />
                    ))}
                  </div>

                  <p className="text-gray-600 text-sm md:text-base leading-relaxed line-clamp-5 italic">
                    "{review.text}"
                  </p>
                </div>

                <div className="mt-6 pt-4 border-t border-gray-100/60 flex justify-between items-center">
                  <span className="text-[9px] font-bold text-gray-300 uppercase tracking-[0.2em]">
                    Verified Review
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default GoogleReviews;